/**
 * upload.service.ts
 * -----------------
 * Angular service for Module 4 — Document Upload & Artifact Storage.
 *
 * Endpoints used:
 *   POST   /api/v1/sessions/upload       — create a new session
 *   GET    /api/v1/sessions/{id}         — fetch session metadata
 *   PATCH  /api/v1/sessions/{id}         — update session (edit + add/delete files)
 *   DELETE /api/v1/sessions/{id}         — delete session and all blobs
 *
 * The MSAL interceptor (configured in app.config.ts) automatically attaches
 * the Bearer token to requests matching the backend API URI pattern.
 */

import { Injectable }                              from '@angular/core';
import { HttpClient, HttpErrorResponse }           from '@angular/common/http';
import { Observable, throwError }                  from 'rxjs';
import { catchError }                              from 'rxjs/operators';
import { environment }                             from '../../../environments/environment';

// ---------------------------------------------------------------------------
// DTOs — mirror the backend Pydantic schemas
// ---------------------------------------------------------------------------

/** Mirrors document-upload.component.ts → UploadForm interface. */
export interface ApplicationDetailsPayload {
  persona:         string;
  analystName:     string;
  catId:           string;
  appId:           string;
  appName:         string;
  managementStyle: string;
  appTypes:        string[];
  tier:            string;
  slLevel:         string | null;
  archDescription: string;
  additionalInfo:  string;
  linkCount:       number;
  links:           string[];   // flatten LinkEntry[] → string[]
}

export interface UploadedFileInfo {
  original_filename: string;
  stored_filename:   string;
  blob_path:         string;
  mime_type:         string;
  size_bytes:        number;
  sha256:            string;
}

export interface UploadSessionResponse {
  success:            boolean;
  message:            string;
  session_id:         string;
  metadata_blob_path: string;
  uploaded_files:     UploadedFileInfo[];
  created_at:         string;
  /** Webpage-to-PDF conversion failures. Empty array when all URLs succeeded. */
  webpage_errors:     Array<{ url: string; error: string }>;
}

export interface SessionMetadataResponse {
  success:             boolean;
  message:             string;
  session_id:          string;
  created_at:          string;
  application_details: ApplicationDetailsPayload;
  uploaded_files:      UploadedFileInfo[];
}

export interface UpdateSessionResponse {
  success:        boolean;
  message:        string;
  session_id:     string;
  uploaded_files: UploadedFileInfo[];
  updated_at:     string;
  /** Webpage-to-PDF conversion failures. Empty array when all URLs succeeded. */
  webpage_errors: Array<{ url: string; error: string }>;
}

export interface DeleteSessionResponse {
  success:       boolean;
  message:       string;
  session_id:    string;
  blobs_deleted: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class UploadService {

  private readonly _base = `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/sessions`;

  constructor(private readonly http: HttpClient) {}

  // ── Create ───────────────────────────────────────────────────────────────

  /**
   * Submits the Module 4 form to the backend to create a new session.
   *
   * @param appDetails    Validated application details from the form.
   * @param archFile      Optional architecture diagram file (single).
   * @param artifactFiles Zero or more artifact documents.
   * @param oldSessionId  If provided, the backend will delete this session first
   *                      (covers the page-reload case: feature #2).
   */
  uploadSession(
    appDetails:    ApplicationDetailsPayload,
    archFile:      File | null,
    artifactFiles: File[],
    oldSessionId?: string | null,
  ): Observable<UploadSessionResponse> {

    const formData = new FormData();
    formData.append('application_details', JSON.stringify(appDetails));

    if (archFile) {
      formData.append('arch_file', archFile, archFile.name);
    }
    for (const file of artifactFiles) {
      formData.append('artifact_files', file, file.name);
    }
    if (oldSessionId) {
      formData.append('old_session_id', oldSessionId);
    }

    return this.http
      .post<UploadSessionResponse>(`${this._base}/upload`, formData)
      .pipe(catchError((err: HttpErrorResponse) => this._handleError(err)));
  }

  // ── Read ─────────────────────────────────────────────────────────────────

  /**
   * Fetches the persisted session metadata from the backend.
   * Used when the user navigates back to the document-upload page.
   */
  getSession(sessionId: string): Observable<SessionMetadataResponse> {
    return this.http
      .get<SessionMetadataResponse>(`${this._base}/${sessionId}`)
      .pipe(catchError((err: HttpErrorResponse) => this._handleError(err)));
  }

  // ── Update ───────────────────────────────────────────────────────────────

  /**
   * Updates an existing session with new application details and/or files.
   *
   * @param sessionId       The session to update.
   * @param appDetails      Full (possibly edited) application details.
   * @param filesToDelete   stored_filename values of blobs to remove.
   * @param newArchFile     Optional new architecture diagram.
   * @param newArtifacts    Additional artifact files to upload.
   */
  updateSession(
    sessionId:     string,
    appDetails:    ApplicationDetailsPayload,
    filesToDelete: string[],
    newArchFile:   File | null,
    newArtifacts:  File[],
  ): Observable<UpdateSessionResponse> {

    const formData = new FormData();
    formData.append('application_details', JSON.stringify(appDetails));
    formData.append('files_to_delete', JSON.stringify(filesToDelete));

    if (newArchFile) {
      formData.append('new_arch_file', newArchFile, newArchFile.name);
    }
    for (const file of newArtifacts) {
      formData.append('new_artifact_files', file, file.name);
    }

    return this.http
      .patch<UpdateSessionResponse>(`${this._base}/${sessionId}`, formData)
      .pipe(catchError((err: HttpErrorResponse) => this._handleError(err)));
  }

  // ── Delete ───────────────────────────────────────────────────────────────

  /** Delete a session and all its blobs from the backend. */
  deleteSession(sessionId: string): Observable<DeleteSessionResponse> {
    return this.http
      .delete<DeleteSessionResponse>(`${this._base}/${sessionId}`)
      .pipe(catchError((err: HttpErrorResponse) => this._handleError(err)));
  }

  // ── Error handling ────────────────────────────────────────────────────────

  private _handleError(err: HttpErrorResponse): Observable<never> {
    let message: string;

    if (err.status === 0) {
      message = 'Could not reach the server. Please check your network connection.';
    } else if (err.status === 400 || err.status === 422) {
      message = err.error?.message ?? 'The submission was rejected. Please check your inputs.';
    } else if (err.status === 413) {
      message = 'The total file size exceeds the allowed limit (500 MB).';
    } else if (err.status === 429) {
      message = 'Too many requests. Please wait a moment before trying again.';
    } else if (err.status >= 500) {
      message = 'A server error occurred. Please try again later.';
    } else {
      message = err.error?.message ?? 'An unexpected error occurred.';
    }

    return throwError(() => new Error(message));
  }
}

